import { useCallback, useRef, useState } from 'react';

import {
  addDays,
  getISODay,
  isWithinInterval,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  subDays,
} from 'date-fns';
import { isEqual } from 'lodash-es';
import { compose } from 'utils';

import { IWeekDays } from 'generated/sanity-graphql';
import useInterval from 'hooks/use-interval';
import logger from 'utils/logger';

import { IDayPartBoundary, IValidDayPart } from '../types';

export const isValidDayPart = (dayPart: any): dayPart is IValidDayPart => {
  return Boolean(dayPart && dayPart.key && dayPart.endTime && dayPart.startTime);
};

// returns a function that converts
// a Date object to the given hour
// and minute on the same day
const setTime = (hour: number, minute: number) =>
  compose<[Date], Date, Date, Date, Date>(
    date => setHours(date, hour),
    date => setMinutes(date, minute),
    date => setSeconds(date, 0),
    date => setMilliseconds(date, 0)
  );

export const transformDateWithTimeString = (timeString: string): Date => {
  const now = new Date();
  if (!timeString) {
    return now;
  }

  const time = timeString.split(':').map(t => parseInt(t, 10));

  if (time.length !== 2 || time.some(t => isNaN(t))) {
    logger.error({ message: 'Received invalid daypart time-string', timeString });
    return now;
  }

  const [hours, minutes] = time;

  return setTime(hours, minutes)(now);
};

function dayPartTimeStringToTodayTime(
  timeString: string,
  tomorrow?: boolean,
  yesterday?: boolean
): Date {
  const transformedDate = transformDateWithTimeString(timeString);
  if (tomorrow) {
    return addDays(transformedDate, 1);
  }

  if (yesterday) {
    return subDays(transformedDate, 1);
  }

  return transformedDate;
}

export const getWeekDay = (date: Date) => {
  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const weekDayIndex = getISODay(date) - 1;
  return weekDays[weekDayIndex];
};

export const isDisabledOnWeekDay = (
  date: Date,
  disabledWeekDays: IWeekDays | null | undefined
): boolean => {
  if (!disabledWeekDays) {
    return false;
  }
  const weekDay = getWeekDay(date);
  return Boolean(disabledWeekDays[weekDay as keyof IWeekDays]);
};

/**
 * Computes the boundaries of day parts based on the provided day parts.
 *
 * @param {ReadonlyArray<IValidDayPart>} dayParts - Array of valid day parts.
 * @returns {IDayPartBoundary[]} - Array of day part boundaries.
 */
const computeDayPartBoundaries = (dayParts: ReadonlyArray<IValidDayPart>) => {
  const now = new Date();
  return dayParts.flatMap<IDayPartBoundary>(dayPart => {
    if (!isValidDayPart(dayPart)) {
      return [];
    }

    const { endTime, key, startTime, weekDays } = dayPart;
    const startTimeDate = transformDateWithTimeString(startTime);
    const endTimeDate = transformDateWithTimeString(endTime);

    const isStartingAfterEndTime = startTimeDate > endTimeDate;

    // If startTime is after endTime, the endTime is tomorrow. However, it's only tomorrow if we are currently past the endTime
    const isEndTimeTomorrow = isStartingAfterEndTime && now > endTimeDate;
    // If startTime is after endTime, the startTime is tomorrow. However, it's only tomorrow if we are currently before the endTime
    const isStartTimeTomorrow = isStartingAfterEndTime && now < endTimeDate;

    return [
      {
        endTime: dayPartTimeStringToTodayTime(endTime, isEndTimeTomorrow),
        key,
        startTime: dayPartTimeStringToTodayTime(startTime, false, isStartTimeTomorrow),
        weekDays,
      },
    ];
  });
};

/**
 * Computes the active day parts based on the current time and day part boundaries.
 *
 * @param {IDayPartBoundary[]} dayPartBoundaries - Array of day part boundaries.
 * @param {Date} now - Current date and time.
 * @returns {IDayPartBoundary[]} - Array of active day part boundaries.
 */
const computeActiveDayParts = (
  dayPartBoundaries: IDayPartBoundary[],
  now: Date
): IDayPartBoundary[] => {
  if (!dayPartBoundaries.length) {
    return [];
  }

  try {
    return dayPartBoundaries.filter(
      ({ endTime, startTime, weekDays }) =>
        isWithinInterval(now, { start: startTime, end: endTime }) &&
        !isDisabledOnWeekDay(now, weekDays)
    );
  } catch (error) {
    logger.error({ dayPartBoundaries, error, message: 'Error finding active dayparts' });
    return [];
  }
};

// 60 (s) * 1000 (ms)
export const REFRESH_INTERVAL = 60 * 1000;

// Boundaries resolve relative to `new Date()` at call time, so they must be recomputed on every
// refresh, not just when `dayParts` changes - otherwise a session left open across midnight
// keeps stale boundaries and an overnight/all-day part never rolls over to the new day.
const deriveActiveDayParts = (dayParts: ReadonlyArray<IValidDayPart>): IDayPartBoundary[] => {
  const newDayPartBoundaries = computeDayPartBoundaries(dayParts);
  return computeActiveDayParts(newDayPartBoundaries, new Date());
};

/**
 * Computes and manages active day parts from the current time and provided day parts.
 *
 * `activeDayParts` updates synchronously (during render) when `dayParts` changes identity, so
 * callers never see a stale value left over from a prior array. The periodic refresh only calls
 * setState when the recomputed value actually differs, avoiding needless re-renders of
 * consumers that publish an un-memoized context value.
 *
 * @param {ReadonlyArray<IValidDayPart>} dayParts - Array of valid day parts.
 * @param {boolean} isEnabled - Whether the periodic legacy refresh should run.
 * @returns {[IDayPartBoundary[], () => void]} - Array of active day part boundaries and a function to force refresh.
 */
export default function useActiveDayParts({
  dayParts,
  isEnabled = true,
}: {
  dayParts: ReadonlyArray<IValidDayPart>;
  isEnabled?: boolean;
}): [IDayPartBoundary[], () => void] {
  const [activeDayParts, setActiveDayParts] = useState(() => deriveActiveDayParts(dayParts));
  const lastRefreshTimeRef = useRef<Date | null>(null);
  const lastDayPartsRef = useRef(dayParts);

  // `dayParts` changing identity must be reflected immediately, in the same render - not on the
  // next effect/interval tick. This is React's "adjusting state during render" pattern:
  // comparing against a ref and calling setState conditionally during render is safe.
  if (lastDayPartsRef.current !== dayParts) {
    lastDayPartsRef.current = dayParts;
    const newActiveDayParts = deriveActiveDayParts(dayParts);
    if (!isEqual(activeDayParts, newActiveDayParts)) {
      setActiveDayParts(newActiveDayParts);
    }
  }

  // Forces a refresh if more than REFRESH_INTERVAL has passed since the last one. Only updates
  // state when the recomputed value actually differs, to avoid re-rendering consumers every tick.
  const forceRefreshActiveDayParts = useCallback(() => {
    const now = new Date();
    setActiveDayParts(currentActiveDayParts => {
      if (
        currentActiveDayParts.length !== 0 &&
        lastRefreshTimeRef.current &&
        now.getTime() - lastRefreshTimeRef.current.getTime() <= REFRESH_INTERVAL
      ) {
        return currentActiveDayParts;
      }
      lastRefreshTimeRef.current = now;
      const newActiveDayParts = deriveActiveDayParts(dayParts);
      return isEqual(currentActiveDayParts, newActiveDayParts)
        ? currentActiveDayParts
        : newActiveDayParts;
    });
  }, [dayParts]);

  // Periodically calls the force refresh function every REFRESH_INTERVAL seconds
  useInterval(
    () => {
      forceRefreshActiveDayParts();
    },
    isEnabled ? REFRESH_INTERVAL : null
  );

  return [activeDayParts, forceRefreshActiveDayParts];
}
